import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import Badge from 'ui/Badge';
import Button from 'ui/Button';
import Icon from 'ui/Icon';
import Input from 'ui/Input';
import { HStack, VStack } from 'ui/Layout';
import Link from 'ui/Link';
import Popover from 'ui/Popover';
import Tooltip from 'ui/Tooltip';
import { Text } from 'ui/Typography';

import { TeamSettings } from '../../utils/team';

type Props = {
  form: ReturnType<typeof useForm<TeamSettings>>;
  colorIndex: number;
};

const ColorKeywordsEditor: React.FC<Props> = ({ form, colorIndex }) => {
  const [newKeyword, setNewKeyword] = useState('');

  const brandColor = form.watch(`brand.colors.${colorIndex}`);
  const hasKeywords = brandColor.keywords.length > 0;

  const trigger = hasKeywords ? (
    <Link fontSize="xs" variant="neutral" whiteSpace="nowrap">
      <Icon icon="pen" /> Edit keywords ({brandColor.keywords.length})
    </Link>
  ) : (
    <Link fontSize="xs" variant="neutral" whiteSpace="nowrap">
      <Icon icon="plus" /> Add keywords
    </Link>
  );

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setNewKeyword('');
    // Add keyword only if it's not empty and not already in the list
    if (newKeyword && !brandColor.keywords.includes(newKeyword)) {
      form.setValue(
        `brand.colors.${colorIndex}.keywords`,
        [...brandColor.keywords, newKeyword],
        { shouldDirty: true }
      );
    }
  };

  return (
    <Popover.Root autoFocus>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Content portalled={false}>
        <Popover.Body p={3}>
          <VStack alignItems="stretch" gap={4}>
            <Text color="gray.800" fontSize="xs">
              New nodes containing any of these keywords will be initially
              assigned <b>{brandColor.label ?? brandColor.value}</b> color.
            </Text>
            {hasKeywords && (
              <HStack flexWrap="wrap" gap={1}>
                {brandColor.keywords?.map((keyword) => (
                  <Badge
                    key={keyword}
                    background="gray.50"
                    border="1px solid"
                    borderColor="gray.300"
                    color="gray.700"
                    fontWeight="normal"
                    size="lg"
                  >
                    {keyword}{' '}
                    <Tooltip content="Remove keyword">
                      <Icon
                        cursor="pointer"
                        color="gray.600"
                        icon="close"
                        onClick={() => {
                          form.setValue(
                            `brand.colors.${colorIndex}.keywords`,
                            brandColor.keywords.filter((k) => k !== keyword),
                            { shouldDirty: true }
                          );
                        }}
                      />
                    </Tooltip>
                  </Badge>
                ))}
              </HStack>
            )}
            <form onSubmit={handleSubmit}>
              <HStack>
                <Input
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  placeholder="Type a keyword"
                  size="sm"
                />
                <Button
                  size="sm"
                  type="submit"
                  variant="primary-ghost"
                  disabled={!newKeyword}
                >
                  <Icon icon="plus" /> Add keyword
                </Button>
              </HStack>
            </form>
          </VStack>
        </Popover.Body>
      </Popover.Content>
    </Popover.Root>
  );
};

export default ColorKeywordsEditor;
